"use client";
import React, { useState, useEffect, useRef } from 'react';
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import ProjectService from '@/app/services/projects';
import LoadingProjects from './components/LoadingProjects';
import NoProjects from './components/NoProjects';
import convertStringToSlug from '@/app/lib/urlFormatter';

export default function Projects({ year, setYear }: { year: number, setYear: any }) {
    const t = useTranslations('Home');
    const endYear = 2015;
    const locale = useLocale();
    const lastFetchedYears = useRef("");
    const [data, setData] = useState<any[]>([])
    const [selectedYears, setSelectedYears] = useState<number[]>([])
    const [error, setError] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [activeType, setActiveType] = useState(typeof window !== 'undefined' ? localStorage.getItem("gfenetre_type") : null);


    const projectRefs = useRef<any>({});

    const groupProjectsByTwo = () => {
        const slicedProjects = data?.slice(2);
        const grouped = [];
        for (let i = 0; i < slicedProjects.length; i += 2) {
            grouped.push(slicedProjects.slice(i, i + 2));
        }
        return grouped;
    };

    function checkHasDataByYear(){
        if(year > 2015){
            setYear(year-1)
        } 
    }

    async function getProjectsByYear() {
    const currentYearsString = JSON.stringify(selectedYears);
    
    if (isLoading || (lastFetchedYears.current === currentYearsString && !activeType)) return;

    setIsLoading(true);
    try {
        const body = {
            year: selectedYears?.length > 0 ? selectedYears : [year],
            language: locale,
            type_id: (!activeType || activeType === 'null') ? null : activeType,
            no_pagination: 1
        };

        const result: any = await ProjectService.getList(body);
        
        if (result?.data?.success) {
            if (result?.data?.data?.length <= 0) {
                setData([])
                checkHasDataByYear();
            } else {
                setData(result?.data?.data);
                lastFetchedYears.current = currentYearsString;
            }
            setError(false);
        } else {
            setError(true);
        }
    } catch (error: any) {
        setError(true);
    } finally {
        setIsLoading(false);
    }
}

    useEffect(() => {
        const observerCallback = (entries: any) => {
            entries.forEach((entry: any) => {
                if (entry.isIntersecting) {
                    const year = entry.target.dataset.year;
                    setYear(parseInt(year));
                }

                const isLastItem = entry.target === projectRefs.current[data[data.length - 1]?.id];
            
                if (entry.isIntersecting && isLastItem && year > endYear) {
                    setYear((prevYear: number) => prevYear - 1);
                    if(!selectedYears?.includes(year-1)){
                        setSelectedYears([...selectedYears, year-1])
                    }
                }
            });
        };
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.5,
        };
        const observer = new IntersectionObserver(observerCallback, observerOptions);

        data.forEach((project: any) => {
            const element = projectRefs.current[project.id];
            if (element) {
                observer.observe(element);
            }
        });

        return () => {
            observer.disconnect();
        };

    }, [data]);

    useEffect(() => {
        const syncStorage = (e: StorageEvent) => {
            if (e.key === "gfenetre_type") {
                setActiveType(e.newValue);
            }
        };
        window.addEventListener("storage", syncStorage);
        return () => window.removeEventListener("storage", syncStorage);
    }, []);

    useEffect(() => {
    if (year && !selectedYears.includes(year)) {
        setSelectedYears(prev => [...prev, year]);
    }
    }, [year]); 

    useEffect(() => {
        getProjectsByYear();
    }, [selectedYears, activeType]);


    return (
        <div className="pb-10">
            {(isLoading && data?.length < 1) ? (
                <LoadingProjects displayFull={true}/>
            ) : (
                <>
                    {(data?.length <= 0 && !isLoading) && (<NoProjects />)}
                    <div className="grid grid-cols-1 md:grid-cols-1">
                        {data?.map((item, i: number) => (
                            <div key={i}>
                                {(i == 0 && item?.orientation == 'horizontal') ? (
                                    <Link key={i} data-is-last={i === data.length - 1} ref={(el: HTMLAnchorElement | null) => { projectRefs.current[item.id] = el }} data-year={item?.completion_year} href={`/projects/${convertStringToSlug(item?.category[`name_${locale}`])}/${convertStringToSlug(item?.translations[locale]?.location)}/${convertStringToSlug(item?.translations[locale]?.name)}/${item?.id}`} target="_blank" className="w-full col-span-12 grid grid-cols-1 grid relative p-3 md:p-8 border-b-1 border-slate-100">
                                        <Image
                                            src={item?.cover_image[0]?.url?.replace('https://', 'https://') ?? ""}
                                            width={800}
                                            height={480}
                                            quality={30}
                                            alt={`Project ${item?.translations[locale]?.name}`}
                                            className="w-full h-auto"
                                            style={{ objectFit: "cover", width: '100%' }}
                                            sizes="(max-width: 768px) 100vw, 33vw"
                                        />
                                        <h1 className="absolute left-5 md:left-16 text-white bottom-20 md:bottom-30 text-4xl font-semibold">{item?.translations[locale]?.name}</h1>
                                        <h1 className="absolute left-5 md:left-16 text-white bottom-12 md:bottom-22 text-3xl font-light">{item?.category[`name_${locale}`]}</h1>
                                        <h6 className="absolute left-5 md:left-16 bg-white px-4 rounded-full bottom-5 md:bottom-15 text-sm font-light">{item?.translations[locale]?.status}</h6>
                                        <h6 className="absolute right-5 md:right-16 bg-dark px-4 rounded-full bottom-5 md:bottom-15 text-sm font-light text-white">{item?.completion_year}</h6>
                                    </Link>
                                ) : (
                                    <>
                                        {(i == 0) && (
                                            <Link href={`/projects/${convertStringToSlug(item?.category[`name_${locale}`])}/${convertStringToSlug(item?.translations[locale]?.location)}/${convertStringToSlug(item?.translations[locale]?.name)}/${item?.id}`} target='_blank'>
                                                <div key={i} data-is-last={i === data.length - 1} className="w-full col-span-12 md:col-span-12 grid grid-cols-12" ref={(el: HTMLDivElement | null) => { projectRefs.current[item.id] = el }} data-year={item?.completion_year}>
                                                    <div className="col-span-12 md:col-span-6 border-r-1 border-slate-100 h-full p-3 md:p-8 justify-between flex flex-col">
                                                        <div>
                                                            <Image
                                                                src={item?.category?.icon?.url}
                                                                width={35}
                                                                height={35}
                                                                priority={false}
                                                                alt={`${item?.category[`name_${locale}`]}`}
                                                                className="h-auto"
                                                            />
                                                            <h1 className="text-2xl md:text-4xl font-regular">{item?.translations[locale]?.name}</h1>
                                                            <h1 className="text-xl font-light color-primary-light">{item?.category[`name_${locale}`]}</h1>
                                                        </div>
                                                        <div className="flex w-full justify-between items-end">
                                                            <h6 className="bg-slate-200 px-4 rounded-full text-sm font-light h-min">{item?.translations[locale]?.status}</h6>
                                                            <div>
                                                                <h1 className="font-light color-primary-light">{t('location')}</h1>
                                                                <h1 className="font-medium">{item?.translations[locale]?.location}</h1>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-span-12 md:col-span-6 border-slate-100 p-8 relative">
                                                        <Image
                                                            src={item?.cover_image[0]?.url?.replace('https://', 'https://') ?? ""}
                                                            width={800}
                                                            height={480}
                                                            quality={60}
                                                            alt={`Project ${item?.translations[locale]?.name}`}
                                                            className="w-full h-auto"
                                                            style={{ objectFit: "cover", width: '100%' }}
                                                            sizes="(max-width: 768px) 100vw, 33vw"
                                                        />
                                                        <h6 className="absolute right-16 bg-dark px-4 rounded-full bottom-12 text-sm font-light text-white">{item?.completion_year}</h6>
                                                    </div>
                                                </div>
                                            </Link>
                                        )}
                                    </>
                                )}
                                {i == 1 && (
                                    <Link href={`/projects/${convertStringToSlug(item?.category[`name_${locale}`])}/${convertStringToSlug(item?.translations[locale]?.location)}/${convertStringToSlug(item?.translations[locale]?.name)}/${item?.id}`} target='_blank'>
                                        <div key={i} data-is-last={i === data.length - 1} className="w-full col-span-12 md:col-span-12 grid grid-cols-12" ref={(el: HTMLDivElement | null) => { projectRefs.current[item.id] = el }} data-year={item?.completion_year}>
                                            <div className="col-span-12 md:col-span-6 border-r-1 border-slate-100 h-full p-3 md:p-8 justify-between flex flex-col">
                                                <div>
                                                    <Image
                                                        src={item?.category?.icon?.url}
                                                        width={35}
                                                        height={35}
                                                        priority={false}
                                                        alt={`${item?.category[`name_${locale}`]}`}
                                                        className="h-auto"
                                                    />
                                                    <h1 className="text-2xl md:text-4xl font-regular">{item?.translations[locale]?.name}</h1>
                                                    <h1 className="text-xl font-light color-primary-light">{item?.category[`name_${locale}`]}</h1>
                                                </div>
                                                <div className="flex w-full justify-between items-end">
                                                    <h6 className="bg-slate-200 px-4 rounded-full text-sm font-light h-min">{item?.translations[locale]?.status}</h6>
                                                    <div>
                                                        <h1 className="font-light color-primary-light">{t('location')}</h1>
                                                        <h1 className="font-medium">{item?.translations[locale]?.location}</h1>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-span-12 md:col-span-6 border-slate-100 p-3 md:p-8 relative">
                                                <Image
                                                    src={item?.cover_image[0]?.url?.replace('https://', 'https://') ?? ""}
                                                    width={800}
                                                    height={480}
                                                    quality={60}
                                                    alt={`Project ${item?.translations[locale]?.name}`}
                                                    className="w-full h-auto"
                                                    style={{ objectFit: "cover", width: '100%' }}
                                                    sizes="(max-width: 768px) 100vw, 33vw"
                                                />
                                                <h6 className="absolute right-16 bg-dark px-4 rounded-full bottom-12 text-sm font-light text-white">{item?.completion_year}</h6>
                                            </div>
                                        </div>
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="w-full">
                        {groupProjectsByTwo().map((pair: any[], rowIndex: number) => (
                            <div
                                key={rowIndex}
                                className="w-full grid grid-cols-1 md:grid-cols-2 border-t-1 border-b-1 border-slate-100"
                            >
                                {pair.map((item: any, colIndex: number) => (
                                    <Link key={colIndex} href={`/projects/${convertStringToSlug(item?.category[`name_${locale}`])}/${convertStringToSlug(item?.translations[locale]?.location)}/${convertStringToSlug(item?.translations[locale]?.name)}/${item?.id}`} target='_blank'>
                                        <div
                                            key={item.id}
                                            data-is-last={colIndex === data.length - 1}
                                            data-year={item?.completion_year}
                                            ref={(el: HTMLDivElement | null) => { projectRefs.current[item.id] = el }}
                                            className={`
                                                    col-span-1
                                                    border-slate-100 
                                                    ${colIndex === 0 && pair.length === 2 ? 'md:border-r-1' : ''} 
                                                    p-0 md:p-8
                                                `}
                                        >
                                            <div className={`
                                                        col-span-12 md:col-span-6 px-3 md:p-8 border-slate-100 relative
                                                    `}>
                                                <Image
                                                    src={item?.cover_image[0]?.url?.replace('https://', 'https://') ?? ""}
                                                    width={800}
                                                    height={480}
                                                    alt={`Project ${item?.translations[locale]?.name}`}
                                                    className="w-full h-auto"
                                                    priority={false}
                                                    quality={60}
                                                    style={{ objectFit: "cover", width: '100%' }}
                                                    sizes="(max-width: 768px) 100vw, 33vw"
                                                />
                                                <h6 className="absolute right-16 bg-dark px-4 rounded-full bottom-12 text-sm font-light text-white">{item?.completion_year}</h6>
                                            </div>

                                            <div className={`col-span-12 md:col-span-6 border-slate-100 p-8 pt-2 justify-between flex flex-col`}>
                                                <div>
                                                    <Image
                                                        src={item?.category?.icon?.url}
                                                        width={35}
                                                        height={35}
                                                        priority={false}
                                                        alt={`${item?.category[`name_${locale}`]}`}
                                                        className="h-auto"
                                                    />
                                                    <h1 className="text-2xl md:text-4xl font-regular">{item?.translations[locale]?.name}</h1>
                                                    <h1 className="text-xl font-light color-primary-light">{item?.category[`name_${locale}`]}</h1>
                                                </div>
                                                <div className="flex w-full justify-between items-end">
                                                    <h6 className="bg-slate-200 px-4 rounded-full text-sm font-light h-min">{item?.translations[locale]?.status}</h6>
                                                    <div>
                                                        <h1 className="font-light color-primary-light">{t('location')}</h1>
                                                        <h1 className="font-medium">{item?.translations[locale]?.location}</h1>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ))}
                    </div>

                    {isLoading && (<LoadingProjects displayFull={false}/>)}
                </>
            )}
        </div>
    );
}
